import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

import { KickstartService } from '../../../../core/kickstart/kickstart.service';
import { OpsSummaryService } from '../../../../core/ops/ops-summary.service';
import { aiRecommendationLabel } from '../../../../core/status/status-meta.types';
import {
  KsApplicationOps,
  KsApplicationStage,
  KS_STAGE_META,
} from '../../../../core/kickstart/kickstart.types';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { IconComponent } from '../../../../shared/icon/icon';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';

@Component({
  selector: 'app-ks-queue',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    RouterLink,
    DatePipe,
    StatusChipComponent,
    IconComponent,
    ZarPipe,
    DashPipe,
  ],
  templateUrl: './ks-queue.html',
  styleUrl: './ks-queue.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsQueueComponent implements OnInit {
  private readonly kickstartService = inject(KickstartService);
  private readonly summaryService = inject(OpsSummaryService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly applications = signal<KsApplicationOps[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  /** Optional ?stage= filter from the URL (e.g. linked from the admin dashboard). */
  protected readonly stageFilter = signal<KsApplicationStage | null>(null);

  protected readonly pageSize = signal(25);
  protected readonly pageIndex = signal(0);

  /** Applications after the active stage filter is applied. */
  protected readonly filteredApplications = computed(() => {
    const stage = this.stageFilter();
    const all = this.applications();
    return stage ? all.filter((a) => a.application_stage === stage) : all;
  });

  protected readonly stageFilterLabel = computed(() => {
    const stage = this.stageFilter();
    return stage ? KS_STAGE_META[stage].label : null;
  });

  protected readonly pagedApplications = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.filteredApplications().slice(start, start + this.pageSize());
  });

  protected readonly columns = [
    'entity',
    'amount',
    'ai_recommendation',
    'stage',
    'submitted',
    'assignee',
    'action',
  ];

  /** short_id of the pick-up in flight, if any. */
  protected readonly pickingUp = signal<string | null>(null);

  /** 5 placeholder rows for the loading skeleton. */
  protected readonly skeletonRows = [1, 2, 3, 4, 5];

  ngOnInit(): void {
    // React to ?stage= so deep-links (e.g. admin dashboard cards) land filtered.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const stage = params.get('stage') as KsApplicationStage | null;
      this.stageFilter.set(stage && stage in KS_STAGE_META ? stage : null);
      this.pageIndex.set(0);
      this.cdr.markForCheck();
    });
    this.load();
  }

  protected readonly aiRecLabel = aiRecommendationLabel;

  /** Pick up is offered on a submitted file nobody has picked up yet. */
  protected canPickUp(app: KsApplicationOps): boolean {
    return app.application_stage === 'SUBMITTED' && app.assignee === null;
  }

  /** The dashboard worklist's pick-up (OpsPickUpView), on this list's rows. */
  protected pickUp(app: KsApplicationOps): void {
    if (this.pickingUp()) return;
    this.pickingUp.set(app.short_id);
    this.summaryService
      .pickUp('KS', app.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.applications.update((all) =>
            all.map((a) =>
              a.short_id === app.short_id
                ? { ...a, assignee: res.assignee_id, assignee_name: res.assignee }
                : a,
            ),
          );
          this.pickingUp.set(null);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.pickingUp.set(null);
          // 409: someone else picked it up first — show who.
          if (err?.status === 409) this.load();
          this.cdr.markForCheck();
        },
      });
  }

  protected clearFilter(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { stage: null },
      queryParamsHandling: 'merge',
    });
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.applications.set([]);
    this.cdr.markForCheck();
    this.load();
  }

  protected onPage(event: PageEvent): void {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  private load(): void {
    this.kickstartService
      .opsListApplications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.applications.set(res.applications);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
